import React from 'react';
import { Card, Box, Typography, CardMedia } from '@mui/material';
import { Utensils, Camera, Copy, Trash2, Pencil, GripVertical } from 'lucide-react';

export default function MenuCard({
  item,
  onImageClick,
  onDuplicate,
  onDelete,
  onEdit,
  onClick,
  children,
  draggableProps,
  innerRef
}) {
  const handleDragHandlePointerDown = (event) => {
    if (draggableProps?.onPointerDown) {
      draggableProps.onPointerDown(event);
    }
    event.stopPropagation();
  };

  return (
    <Card
      ref={innerRef}
      onClick={onClick}
      sx={{
        width: 250,
        height: 340,
        p: 0.5,
        pt: 2,
        borderRadius: 3,
        boxShadow: 3,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        transition: 'transform 0.3s ease, box-shadow 0.3s ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: 6,
        },
        position: 'relative',
        cursor: 'pointer'
      }}
    >

      {/* Drag handle (designated reorder button only) */}
      <Box
        {...(draggableProps || {})}
        onPointerDown={handleDragHandlePointerDown}
        sx={{
          position: 'absolute',
          left: -6,
          top: '50%',
          transform: 'translateY(-50%)',
          width: 24,
          height: 24,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: 999,
          cursor: 'grab',
          color: '#9CA3AF',
          zIndex: 3,
          touchAction: 'none',
          '&:active': { cursor: 'grabbing' },
        }}
        title="Drag to reorder"
        aria-label="Drag to reorder"
        role="button"
      >
        <GripVertical size={14} />
      </Box>

      {/* Hover actions */}
      <Box
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          opacity: 0,
          transition: 'opacity 0.3s ease',
          display: 'flex',
          gap: 1,
          zIndex: 2,
          pointerEvents: 'none',
          '.MuiCard-root:hover &': { opacity: 1 }
        }}
      >
        {/* Edit */}
        <Box
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => { e.stopPropagation(); onEdit?.(item); }}
          sx={{
            width: 24,
            height: 24,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            zIndex: 20,
            pointerEvents: 'auto',
            transition: 'transform 0.2s ease',
            '&:hover': { transform: 'scale(1.1)' }
          }}
          title="Edit"
        >
          <Pencil size={16} />
        </Box>
        {/* Duplicate */}
        <Box
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => { e.stopPropagation(); onDuplicate?.(item); }}
          sx={{
            width: 24,
            height: 24,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            zIndex: 20,
            pointerEvents: 'auto',
            transition: 'transform 0.2s ease',
            '&:hover': { transform: 'scale(1.1)' }
          }}
          title="Duplicate"
        >
          <Copy size={16} />
        </Box>
        {/* Delete */}
        <Box
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => { e.stopPropagation(); onDelete?.(item); }}
          sx={{
            width: 24,
            height: 24,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            zIndex: 20,
            pointerEvents: 'auto',
            transition: 'transform 0.2s ease',
            '&:hover': { transform: 'scale(1.1)' }
          }}
          title="Delete"
        >
          <Trash2 size={16} />
        </Box>
      </Box>

      {/* Image */}
      <Box
        onClick={onImageClick}
        onPointerDown={(e) => e.stopPropagation()}
        sx={{
          width: 120,
          height: 120,
          borderRadius: '50%',
          overflow: 'hidden',
          mx: 'auto',
          mb: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f0f0f0',
          cursor: 'pointer',
          position: 'relative',
          '&:hover img': {
            transform: 'scale(1.1)',
            filter: 'brightness(0.8)',
          },
          '&:hover .overlay': {
            opacity: 1,
          }
        }}
      >
        {item.image ? (
          <>
            <CardMedia
              component="img"
              image={item.image}
              alt={item.name}
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                transition: 'transform 0.4s ease, filter 0.4s ease',
              }}
            />
            <Box
              className="overlay"
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(0, 0, 0, 0.3)',
                opacity: 0,
                transition: 'opacity 0.3s ease',
              }}
            >
              <Box sx={{ color: '#fff', opacity: 0.8 }}>
                <Camera size={26} />
              </Box>
            </Box>
          </>
        ) : (
          <>
            <Utensils size={24} color="#888" />
            <Box
              className="overlay"
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(0, 0, 0, 0.3)',
                opacity: 0,
                transition: 'opacity 0.3s ease',
              }}
            >
              <Box sx={{ color: '#fff', opacity: 0.8 }}>
                <Camera size={26} />
              </Box>
            </Box>
          </>
        )}
      </Box>

      {children}
    </Card>
  );
}
